import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../services";
import type { ListInvitesResult, ListResetCodesResult, ListUsersResult, SectionInfo } from "../services/types";
import { useSession } from "../components/SessionContext";
import { PageHead, SectionHead } from "../components/dash/PageHead";
import { DataTable, type Column } from "../components/dash/DataTable";
import { formatISTDateTime } from "../lib/readAgo";

// Admin: users and their sections, invite codes, reset codes. The mutations
// keep the original tool surface; only the markup changed.

type User = ListUsersResult["users"][number];
type Invite = ListInvitesResult["invites"][number];
type Reset = ListResetCodesResult["resets"][number];

export function Admin() {
  const session = useSession();
  const qc = useQueryClient();
  const [resetEmail, setResetEmail] = useState("");
  const [newInvite, setNewInvite] = useState<string | null>(null);
  const [newReset, setNewReset] = useState<{ email: string; code: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const usersQ = useQuery({
    queryKey: ["admin:listUsers"],
    queryFn: async () => {
      const r = await api.admin.listUsers();
      if (!r.ok) throw new Error(r.error);
      const d = r as unknown as ListUsersResult;
      return { users: d.users ?? [], sections: d.available_sections ?? [] };
    },
  });

  const invitesQ = useQuery({
    queryKey: ["admin:listInvites"],
    queryFn: async () => {
      const r = await api.admin.listInvites();
      if (!r.ok) throw new Error(r.error);
      return (r as unknown as ListInvitesResult).invites ?? [];
    },
  });

  const resetsQ = useQuery({
    queryKey: ["admin:listResetCodes"],
    queryFn: async () => {
      const r = await api.admin.listResetCodes();
      if (!r.ok) throw new Error(r.error);
      return (r as unknown as ListResetCodesResult).resets ?? [];
    },
  });

  // Toggling a section writes the WHOLE list back, because set_user_sections replaces rather than
  // patches. Sending a delta would silently drop every other grant.
  const sectionsMut = useMutation({
    mutationFn: async ({ userId, sections }: { userId: number; sections: string[] }) => {
      const r = await api.admin.setUserSections(userId, sections);
      if (!r.ok) throw new Error(r.error);
      return r;
    },
    onSuccess: async () => {
      setErr(null);
      await qc.invalidateQueries({ queryKey: ["admin:listUsers"] });
    },
    onError: (e) => setErr(`Could not update permissions: ${(e as Error).message}`),
  });

  const generateInviteMut = useMutation({
    mutationFn: async () => {
      const r = await api.admin.generateInvite();
      if (!r.ok) throw new Error(r.error);
      return (r as { code: string }).code;
    },
    onSuccess: async (code) => {
      setErr(null);
      setNewInvite(code);
      await qc.invalidateQueries({ queryKey: ["admin:listInvites"] });
    },
    onError: (e) => setErr((e as Error).message),
  });

  const generateResetMut = useMutation({
    mutationFn: async (targetEmail: string) => {
      const r = await api.admin.generateResetCode(targetEmail);
      if (!r.ok) throw new Error(r.error);
      return (r as { code: string }).code;
    },
    onSuccess: async (code) => {
      setErr(null);
      setNewReset({ email: resetEmail, code });
      setResetEmail("");
      await qc.invalidateQueries({ queryKey: ["admin:listResetCodes"] });
    },
    onError: (e) => setErr((e as Error).message),
  });

  const revokeInviteMut = useMutation({
    mutationFn: async (code: string) => {
      const r = await api.admin.revokeInvite(code);
      if (!r.ok) throw new Error(r.error);
      return r;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin:listInvites"] }),
    onError: (e) => setErr((e as Error).message),
  });

  const revokeResetMut = useMutation({
    mutationFn: async (code: string) => {
      const r = await api.admin.revokeResetCode(code);
      if (!r.ok) throw new Error(r.error);
      return r;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin:listResetCodes"] }),
    onError: (e) => setErr((e as Error).message),
  });

  if (!session || session.role !== "admin") {
    return (
      <>
        <PageHead title="Admin" />
        <p className="empty">This area is for administrators. Sign in with an admin account to manage users, invites and reset codes.</p>
      </>
    );
  }

  const sections: SectionInfo[] = usersQ.data?.sections ?? [];

  // Arrays, sorted on an ISO string.
  const inviteEntries = [...(invitesQ.data ?? [])].sort((a, b) =>
    String(b.created_at ?? "").localeCompare(String(a.created_at ?? "")));
  const resetEntries = [...(resetsQ.data ?? [])].sort((a, b) =>
    String(b.created_at ?? "").localeCompare(String(a.created_at ?? "")));

  const userColumns: Column<User>[] = [
    { key: "email", label: "Email", cell: (u) => u.email },
    { key: "role", label: "Role", cell: (u) => <>{u.role}{u.active ? null : <span className="dim">disabled</span>}</> },
    { key: "joined", label: "Joined IST", cell: (u) => formatISTDateTime(u.created_at) },
    {
      key: "sections", label: "Sections",
      cell: (u) =>
        u.all_sections ? (
          "Every section, by rule."
        ) : (
          <div className="kws">
            {sections.map((sec) => {
              const on = u.sections.includes(sec.key);
              return (
                <button
                  key={sec.key}
                  type="button"
                  className={`kw${on ? " on" : ""}`}
                  aria-pressed={on}
                  title={`${sec.tool_count} tools`}
                  disabled={sectionsMut.isPending}
                  onClick={() =>
                    sectionsMut.mutate({
                      userId: u.id,
                      sections: on ? u.sections.filter((k) => k !== sec.key) : [...u.sections, sec.key],
                    })
                  }
                >
                  {sec.label}
                </button>
              );
            })}
          </div>
        ),
    },
  ];

  const inviteColumns: Column<Invite>[] = [
    { key: "code", label: "Code", cell: (i) => <span className="code">{i.code}</span> },
    {
      key: "status", label: "Status",
      cell: (i) => (i.used_by ? `Used by ${i.used_by}${i.used_at ? ` on ${formatISTDateTime(i.used_at)}` : ""}` : "Unused"),
    },
    { key: "created", label: "Created IST", cell: (i) => formatISTDateTime(i.created_at) },
    {
      key: "revoke", label: "Revoke",
      cell: (i) =>
        i.used_by ? "" : (
          <button type="button" className="site" disabled={revokeInviteMut.isPending} onClick={() => revokeInviteMut.mutate(i.code)}>
            Revoke
          </button>
        ),
    },
  ];

  const resetColumns: Column<Reset>[] = [
    { key: "code", label: "Code", cell: (r) => <span className="code">{r.code}</span> },
    { key: "for", label: "For", cell: (r) => r.target_email },
    { key: "created", label: "Created IST", cell: (r) => formatISTDateTime(r.created_at) },
    {
      key: "revoke", label: "Revoke",
      cell: (r) => (
        <button type="button" className="site" disabled={revokeResetMut.isPending} onClick={() => revokeResetMut.mutate(r.code)}>
          Revoke
        </button>
      ),
    },
  ];

  return (
    <>
      <PageHead title="Admin" caption="Users and the sections they may reach, invite codes, reset codes." />

      {err ? <div className="note-neg"><p>{err}</p></div> : null}

      <section className="section compact">
        <SectionHead title="Users" />
        {usersQ.isError ? (
          <div className="note-neg"><p>{(usersQ.error as Error).message}</p></div>
        ) : (
          <DataTable
            columns={userColumns}
            rows={usersQ.data?.users ?? []}
            rowKey={(u) => u.email}
            loading={usersQ.isPending}
            loadingText="Reading the user list."
            empty="No users yet."
          />
        )}
      </section>

      <section className="section compact">
        <SectionHead title="Invite codes" />
        <div className="run">
          <button type="button" className="btn" disabled={generateInviteMut.isPending} onClick={() => generateInviteMut.mutate()}>
            {generateInviteMut.isPending ? "Working" : "Generate invite code"}
          </button>
        </div>
        {newInvite ? (
          <div className="note">
            <p>
              New code <strong className="code">{newInvite}</strong>{" "}
              <button type="button" className="site" onClick={() => navigator.clipboard?.writeText(newInvite)}>Copy</button>
            </p>
          </div>
        ) : null}
        {invitesQ.isError ? (
          <div className="note-neg"><p>{(invitesQ.error as Error).message}</p></div>
        ) : (
          <DataTable
            columns={inviteColumns}
            rows={inviteEntries}
            rowKey={(i) => i.code}
            loading={invitesQ.isPending}
            loadingText="Reading the invite list."
            empty="No invites yet."
          />
        )}
      </section>

      <section className="section compact">
        <SectionHead title="Reset codes" />
        <form
          className="inline-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (resetEmail) generateResetMut.mutate(resetEmail);
          }}
        >
          <label className="field">
            <span>Target user email</span>
            <input
              type="email"
              placeholder="name@example.com"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              required
            />
          </label>
          <button type="submit" className="btn" disabled={generateResetMut.isPending || !resetEmail}>
            {generateResetMut.isPending ? "Working" : "Generate reset code"}
          </button>
        </form>
        {newReset ? (
          <div className="note">
            <p>
              Reset for {newReset.email}: <strong className="code">{newReset.code}</strong>{" "}
              <button type="button" className="site" onClick={() => navigator.clipboard?.writeText(newReset.code)}>Copy</button>
            </p>
          </div>
        ) : null}
        {resetsQ.isError ? (
          <div className="note-neg"><p>{(resetsQ.error as Error).message}</p></div>
        ) : (
          <DataTable
            columns={resetColumns}
            rows={resetEntries}
            rowKey={(r) => r.code}
            loading={resetsQ.isPending}
            loadingText="Reading the reset list."
            empty="No active reset codes."
          />
        )}
      </section>
    </>
  );
}
